import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { BRAND_KIT } from "@/constants/brandKitTestIds";
import { _Header as Header, _Empty as Empty } from "./LogosTab";

const PATH = "library";

/**
 * Lightweight brand image library — a small pool of photography the AI
 * Assembly Agent can draw from when a slot wants imagery but no specific
 * Product/Promotion is supplying it. Organised by a free-form
 * `subject_tag` (e.g. "beach", "couple", "cabin").
 */
export default function LibraryTab({ brandId, canManage }) {
  const [items, setItems] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState("");
  const [files, setFiles] = useState([]);
  const [subjectTag, setSubjectTag] = useState("");
  const [alt, setAlt] = useState("");
  const [notes, setNotes] = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const inputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: imgs }, { data: subs }] = await Promise.all([
        api.get(`/brands/${brandId}/brand-kit/${PATH}`),
        api.get(`/brands/${brandId}/brand-kit/${PATH}/subjects`),
      ]);
      setItems(imgs);
      setSubjects(subs);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [brandId]);

  const grouped = useMemo(() => {
    const m = {};
    for (const it of items) {
      const k = it.subject_tag || "(uncategorised)";
      (m[k] = m[k] || []).push(it);
    }
    return Object.entries(m).sort(([a], [b]) => a.localeCompare(b));
  }, [items]);

  const openNew = () => {
    setAdding(true); setError(""); setFiles([]);
    setSubjectTag(""); setAlt(""); setNotes("");
    setProgress({ done: 0, total: 0 });
  };
  const close = () => {
    setAdding(false); setFiles([]);
    if (inputRef.current) inputRef.current.value = "";
  };

  const onPickFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    setFiles(picked);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const tag = subjectTag.trim().toLowerCase();
    if (!tag) { setError("Pick or type a subject tag"); return; }
    if (files.length === 0) { setError("Choose at least one image"); return; }
    setUploading(true);
    setProgress({ done: 0, total: files.length });
    try {
      for (let i = 0; i < files.length; i++) {
        const fd = new FormData();
        fd.append("file", files[i]);
        const { data: asset } = await api.post(
          `/files/upload?brand_id=${brandId}`, fd,
          { headers: { "Content-Type": "multipart/form-data" } },
        );
        await api.post(`/brands/${brandId}/brand-kit/${PATH}`, {
          image_url: asset.url,
          subject_tag: tag,
          alt: alt.trim() || null,
          notes: notes.trim() || null,
          priority: 100,
        });
        setProgress({ done: i + 1, total: files.length });
      }
      close();
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/brands/${brandId}/brand-kit/${PATH}/${id}`);
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || "Could not delete image");
    }
  };

  if (loading) return <p className="text-sm text-[#6B6358]">Loading library…</p>;

  return (
    <div>
      <Header
        title="Photography library"
        subtitle="A small, brand-approved pool of imagery the AI Assembly Agent can draw from when a layout needs photography but no specific product or promotion supplies one. Tag each image with a short subject (e.g. ‘beach’, ‘family’, ‘cabin interior’)."
        canManage={canManage}
        actionTestId={BRAND_KIT.libraryAddButton}
        onAdd={openNew}
        active={adding}
      />

      {adding && (
        <form onSubmit={submit} className="border border-[#1A1F2C]/10 bg-white rounded-lg p-6 mb-6">
          <h3 className="font-serif text-lg mb-4">Add to library</h3>

          <p className="text-xs uppercase tracking-[0.18em] text-[#6B6358] mb-2">Subject tag</p>
          {subjects.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2">
              {subjects.map((s) => (
                <button
                  type="button"
                  key={s.subject_tag}
                  data-testid={`${BRAND_KIT.librarySubjectChipPrefix}${s.subject_tag}`}
                  onClick={() => setSubjectTag(s.subject_tag)}
                  className={`px-3 py-1 rounded-full text-xs uppercase tracking-[0.15em] border ${
                    subjectTag === s.subject_tag
                      ? "border-[#1A1F2C] bg-[#1A1F2C] text-[#F8F5EE]"
                      : "border-[#1A1F2C]/30 text-[#3D3A35]"
                  }`}
                >
                  {s.subject_tag} <span className="opacity-60">· {s.count}</span>
                </button>
              ))}
            </div>
          )}
          <input
            data-testid={BRAND_KIT.librarySubjectInput}
            value={subjectTag}
            onChange={(e) => setSubjectTag(e.target.value)}
            placeholder="e.g. beach"
            className="w-full px-3 py-2 border border-[#1A1F2C]/20 rounded-md text-sm bg-white mb-4"
          />

          <p className="text-xs uppercase tracking-[0.18em] text-[#6B6358] mb-2">Images (you can pick many at once)</p>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            multiple
            data-testid={BRAND_KIT.libraryUploadInput}
            onChange={onPickFiles}
            className="block text-sm mb-2"
          />
          {files.length > 0 && (
            <p className="text-xs text-[#3D3A35] mb-3">
              {files.length} file{files.length === 1 ? "" : "s"} selected.
            </p>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
            <div>
              <p className="text-xs uppercase tracking-[0.18em] text-[#6B6358] mb-1">Alt text (applies to all uploaded)</p>
              <input
                data-testid={BRAND_KIT.libraryAltInput}
                value={alt}
                onChange={(e) => setAlt(e.target.value)}
                placeholder="Short caption — also helps the AI pick the right image"
                className="w-full px-3 py-2 border border-[#1A1F2C]/20 rounded-md text-sm bg-white"
              />
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.18em] text-[#6B6358] mb-1">Usage notes (optional)</p>
              <input
                data-testid={BRAND_KIT.libraryNotesInput}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g. ‘Avoid on disclaimer panel’"
                className="w-full px-3 py-2 border border-[#1A1F2C]/20 rounded-md text-sm bg-white"
              />
            </div>
          </div>

          {error && <p className="text-sm text-[#B5462F] mt-3">{error}</p>}

          <div className="mt-5 flex items-center gap-3">
            <button
              data-testid={BRAND_KIT.librarySubmit}
              type="submit"
              disabled={uploading}
              className="px-4 py-2 bg-[#1A1F2C] text-[#F8F5EE] rounded-full text-xs uppercase tracking-[0.18em] font-medium disabled:opacity-50"
            >
              {uploading
                ? `Uploading ${progress.done}/${progress.total}…`
                : `Add ${files.length || ""} image${files.length === 1 ? "" : "s"}`}
            </button>
            <button
              data-testid={BRAND_KIT.libraryCancel}
              type="button"
              onClick={close}
              className="text-xs uppercase tracking-[0.18em] text-[#6B6358] px-2"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {items.length === 0 ? (
        <Empty testid={BRAND_KIT.libraryEmpty} label="No library images yet. Upload a small pool the agent can borrow from." />
      ) : (
        <div data-testid={BRAND_KIT.libraryList}>
          {grouped.map(([subject, rows]) => (
            <div
              key={subject}
              data-testid={`${BRAND_KIT.librarySectionPrefix}${subject}`}
              className="mb-8"
            >
              <h3 className="text-xs uppercase tracking-[0.22em] text-[#6B6358] mb-3">
                {subject} <span className="text-[#6B6358]">· {rows.length}</span>
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {rows.map((it) => (
                  <div
                    key={it.image_id}
                    data-testid={`${BRAND_KIT.libraryRowPrefix}${it.image_id}`}
                    className="group relative border border-[#1A1F2C]/10 bg-white rounded-lg overflow-hidden"
                  >
                    <div className="aspect-[4/3] bg-[#F8F5EE] flex items-center justify-center">
                      <img
                        src={it.image_url}
                        alt={it.alt || subject}
                        className="max-w-full max-h-full object-cover"
                      />
                    </div>
                    {it.alt && (
                      <p className="text-[11px] text-[#3D3A35] px-2 py-1 truncate" title={it.alt}>{it.alt}</p>
                    )}
                    {canManage && (
                      <button
                        data-testid={`${BRAND_KIT.libraryDeletePrefix}${it.image_id}`}
                        type="button"
                        onClick={() => remove(it.image_id)}
                        className="absolute top-1 right-1 px-2 py-0.5 text-[10px] uppercase tracking-[0.12em] bg-[#B5462F]/90 text-[#F8F5EE] rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
